import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';

export const BeforeAfter = () => {
  const [pos, setPos] = useState(50);
  const containerRef = useRef(null);
  const dragging = useRef(false);

  const handleMove = (clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const newPos = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(newPos);
  };

  return (
    <section
      className="relative py-24 md:py-40 bg-[#F9F7F4] overflow-hidden"
      data-testid="before-after-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid md:grid-cols-12 gap-8 mb-16">
          <div className="md:col-span-5">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center gap-3 mb-6"
            >
              <div className="h-px w-12 bg-[#0D1B2A]" />
              <span className="text-[#0D1B2A] text-xs tracking-[0.4em] uppercase">
                The Difference
              </span>
            </motion.div>
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1 }}
              className="font-serif text-[#0D1B2A] leading-[1] tracking-tight"
              style={{ fontSize: 'clamp(2.5rem, 6vw, 5rem)' }}
            >
              Drag.
              <br />
              <span className="italic font-light">Watch the transformation.</span>
            </motion.h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-base md:text-lg text-[#0D1B2A]/70 max-w-md font-light">
              No retouching. No tricks. Just our team, your space, and four
              hours between the two.
            </p>
          </div>
        </div>

        <div
          ref={containerRef}
          className="relative w-full aspect-[16/10] overflow-hidden cursor-ew-resize select-none shadow-2xl"
          onMouseMove={(e) => dragging.current && handleMove(e.clientX)}
          onMouseDown={(e) => {
            dragging.current = true;
            handleMove(e.clientX);
          }}
          onMouseUp={() => (dragging.current = false)}
          onMouseLeave={() => (dragging.current = false)}
          onTouchMove={(e) => handleMove(e.touches[0].clientX)}
          onTouchStart={(e) => handleMove(e.touches[0].clientX)}
          data-testid="before-after-slider"
        >
          {/* AFTER (clean, vibrant) - bottom layer */}
          <img
            src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1800&q=85&auto=format&fit=crop"
            alt="After cleaning - sparkling clean kitchen"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ filter: 'brightness(1.05) saturate(1.15) contrast(1.05)' }}
          />
          <div className="absolute top-6 right-6 bg-[#C9A84C] text-[#0D1B2A] px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold z-10">
            After · Tsalach
          </div>

          {/* BEFORE (dim, dusty) - top layer with clip */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
          >
            <img
              src="https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=1800&q=85&auto=format&fit=crop"
              alt="Before cleaning - dirty kitchen"
              className="absolute inset-0 w-full h-full object-cover"
              style={{ filter: 'brightness(0.5) saturate(0.55) contrast(0.85) sepia(0.25) blur(0.7px)' }}
            />
            <div className="absolute inset-0 bg-[#0D1B2A]/35 mix-blend-multiply" />
            <div className="absolute top-6 left-6 bg-white/90 text-[#0D1B2A] px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold">
              Before
            </div>
          </div>

          {/* Slider line + handle */}
          <div
            className="absolute top-0 bottom-0 w-0.5 bg-white pointer-events-none"
            style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
          >
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-2xl flex items-center justify-center">
              <div className="flex items-center gap-0.5 text-[#0D1B2A]">
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M9 3L5 7L9 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path d="M5 3L9 7L5 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
